import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, CheckCircle, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const EmailConfirmation: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-pink-100 flex items-center justify-center px-4 py-6 sm:py-12">
      <div className="max-w-md w-full">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-8 text-center">
          {/* Icon */}
          <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-pink-100 to-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <Mail className="w-7 h-7 sm:w-10 sm:h-10 text-pink-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">
            {t('emailConfirmation.title')}
          </h1>

          {/* Description */}
          <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6">
            {t('emailConfirmation.description')}
          </p>

          {/* Steps */}
          <div className="bg-gradient-to-r from-pink-50 to-yellow-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-pink-600 mr-2 flex-shrink-0" />
              {t('emailConfirmation.nextStepsTitle')}
            </h3>
            <ol className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-700">
              <li className="flex items-start">
                <span className="font-bold text-pink-600 mr-2">1.</span>
                <span>{t('emailConfirmation.step1')}</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-pink-600 mr-2">2.</span>
                <span>{t('emailConfirmation.step2')}</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-pink-600 mr-2">3.</span>
                <span>{t('emailConfirmation.step3')}</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-pink-600 mr-2">4.</span>
                <span>{t('emailConfirmation.step4')}</span>
              </li>
            </ol>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-blue-900">
              <strong>{t('emailConfirmation.tipTitle')}</strong> {t('emailConfirmation.tipMessage')}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-2 sm:space-y-3">
            <Link
              to="/login"
              className="w-full bg-gradient-to-r from-pink-600 to-yellow-500 text-white px-6 py-2.5 sm:py-3 rounded-lg font-medium hover:from-pink-700 hover:to-yellow-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center group text-sm sm:text-base"
            >
              <span>{t('emailConfirmation.alreadyConfirmed')}</span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link
              to="/"
              className="block w-full border-2 border-gray-300 text-gray-700 px-6 py-2.5 sm:py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              {t('emailConfirmation.backToHome')}
            </Link>
          </div>
        </div>

        {/* Help Text */}
        <p className="text-center text-gray-600 mt-4 sm:mt-6 text-xs sm:text-sm">
          {t('emailConfirmation.needHelp')}{' '}
          <button 
            onClick={() => navigate('/support')}
            className="text-pink-600 hover:underline font-medium"
          >
            {t('emailConfirmation.contactSupport')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default EmailConfirmation;

